import { useTranslation } from 'react-i18next'
import type { PaginationProps } from '@/shared/types/pagination'

/**
 * Simple pagination UI: Previous button, page indicator, Next button.
 * Only renders when totalPages > 1.
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  previousLabel,
  nextLabel,
  className = '',
}: PaginationProps) {
  const { t } = useTranslation()
  const prev = previousLabel ?? t('common.previous')
  const next = nextLabel ?? t('common.next')

  if (totalPages <= 1) return null

  return (
    <div className={`flex items-center justify-center gap-3 pt-2 ${className}`}>
      <button
        type="button"
        disabled={currentPage === 0}
        onClick={() => { onPageChange(currentPage - 1) }}
        className="rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-150 disabled:opacity-40 active:scale-[0.97] bg-tg-secondary hover:bg-tg-bg"
        style={{
          border: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 20%, transparent)',
        }}
      >
        {prev}
      </button>
      <span
        className="text-sm tabular-nums px-3 py-1 rounded-md"
        style={{
          color: 'var(--tg-theme-hint-color)',
          backgroundColor: 'color-mix(in srgb, var(--tg-theme-hint-color) 8%, transparent)',
        }}
      >
        {currentPage + 1} / {totalPages}
      </span>
      <button
        type="button"
        disabled={currentPage >= totalPages - 1}
        onClick={() => { onPageChange(currentPage + 1) }}
        className="rounded-lg px-3 py-1.5 text-sm font-medium transition-all duration-150 disabled:opacity-40 active:scale-[0.97] bg-tg-secondary hover:bg-tg-bg"
        style={{
          border: '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 20%, transparent)',
        }}
      >
        {next}
      </button>
    </div>
  )
}
